import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IconDashboard, IconNotification, IconBookmark, IconList, IconClipboardList, IconCompass, IconFileInvoice,
    IconSettings, IconUser, IconCar, IconSteeringWheel, IconBriefcase, IconFileText, IconListCheck, IconUsers, IconCurrencyDollar,
    IconBuildingBank, IconLogout, IconReceipt, IconUserPlus, IconCash, IconFileCheck, IconCreditCard, IconCoin, IconUserCheck, IconBus, IconHomeFilled, IconDoor, IconCreditCardPay,
    IconCreditCardRefund, IconCashRegister, IconFileTypography, IconFileDollar, IconHome2, IconCalendarEvent} from '@tabler/icons-react';

// material-ui
import { makeStyles } from '@mui/styles';
import { Avatar, Chip, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';

// project imports
import { MENU_OPEN, SET_MENU } from '../../../../store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// style constant
const useStyles = makeStyles((theme) => ({
    listIcon: {
        minWidth: '18px',
        marginTop: '1px',
        marginBottom: '1px'
    },
    listCustomIconSub: {
        width: '6px',
        height: '6px'
    },
    listCustomIconSubActive: {
        width: '4px',
        height: '4px'
    },
    listItem: {
        marginBottom: '2px',
        alignItems: 'center'
    },
    listItemNoBack: {
        marginBottom: '2px',
        backgroundColor: 'transparent !important',
        paddingTop: '1px',
        paddingBottom: '1px',
        alignItems: 'flex-start'
    },
    subMenuCaption: {
        ...theme.typography.subMenuCaption
    }
}));

//-----------------------|| SIDEBAR MENU LIST ITEMS ||-----------------------//

const NavItem = ({ item, level }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const icons = {
        IconDashboard: IconDashboard,
        IconNotification: IconNotification,
        IconBookmark: IconBookmark,
        IconList: IconList,
        IconClipboardList: IconClipboardList,
        IconCompass: IconCompass,
        IconFileInvoice: IconFileInvoice,
        IconUser: IconUser,
        IconCar: IconCar,
        IconSteeringWheel: IconSteeringWheel,
        IconBriefcase: IconBriefcase,
        IconFileText: IconFileText,
        IconListCheck: IconListCheck,
        IconUsers: IconUsers,
        IconSettings: IconSettings,
        IconBuildingBank: IconBuildingBank,
        IconLogout: IconLogout,
        IconCurrencyDollar: IconCurrencyDollar,
        IconReceipt: IconReceipt,
        IconUserPlus: IconUserPlus,
        IconFileCheck: IconFileCheck,
        IconCash: IconCash,
        IconCreditCard: IconCreditCard,
        IconCoin: IconCoin,
        IconUserCheck: IconUserCheck,
        IconBus: IconBus,
        IconHomeFilled: IconHomeFilled,
        IconDoor: IconDoor,
        IconCreditCardPay: IconCreditCardPay,
        IconCreditCardRefund: IconCreditCardRefund,
        IconCashRegister: IconCashRegister,
        IconFileTypography: IconFileTypography,
        IconFileDollar: IconFileDollar,
        IconHome2: IconHome2,
        IconCalendarEvent: IconCalendarEvent
    };

    const IconComponent = icons[item.icon]

    const itemIcon = item.icon ? (
        IconComponent ? (
            <IconComponent stroke={1.5} size="1.3rem" className={classes.listCustomIcon} />
        ) : <FiberManualRecordIcon
                className={
                    customization.isOpen.findIndex((id) => id === item.id) > -1 ? classes.listCustomIconSubActive : classes.listCustomIconSub
                }
                fontSize={level > 0 ? 'inherit' : 'default'}
            />
    ) : (
        <FiberManualRecordIcon
            className={
                customization.isOpen.findIndex((id) => id === item.id) > -1 ? classes.listCustomIconSubActive : classes.listCustomIconSub
            }
            fontSize={level > 0 ? 'inherit' : 'default'}
        />
    );

    let itemIconClass = !item.icon ? classes.listIcon : classes.menuIcon;
    itemIconClass = customization.navType === 'nav-dark' ? [itemIconClass, classes.listCustomIcon].join(' ') : itemIconClass;

    let itemTarget = '';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps = { component: React.forwardRef((props, ref) => <Link {...props} to={item.url} />) };
    if (item.external) {
        listItemProps = { component: 'a', href: item.url };
    }

    const itemHandler = (id) => {
        dispatch({ type: MENU_OPEN, id: id });
        matchesSM && dispatch({ type: SET_MENU, opened: false });
    };

    // active menu item on page load
    React.useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === item.id);
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
        // eslint-disable-next-line
    }, []);

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            className={level > 1 ? classes.listItemNoBack : classes.listItem}
            sx={{ borderRadius: customization.borderRadius + 'px' }}
            selected={customization.isOpen.findIndex((id) => id === item.id) > -1}
            onClick={() => itemHandler(item.id)}
            target={itemTarget}
            style={{ paddingLeft: level * 23 + 'px' }}
        >
            <ListItemIcon className={itemIconClass}>{itemIcon}</ListItemIcon>
            <ListItemText
                primary={
                    <Typography variant={customization.isOpen.findIndex((id) => id === item.id) > -1 ? 'h5' : 'body1'} color="inherit">
                        {item.title}
                    </Typography>
                }
                secondary={
                    item.caption && (
                        <Typography variant="caption" className={classes.subMenuCaption} display="block" gutterBottom>
                            {item.caption}
                        </Typography>
                    )
                }
            />
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
